import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Award, Download, Loader2, Send } from 'lucide-react';
import { HelpConnectCertificateResend } from './HelpConnectCertificateResend';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_LABEL = { sent: 'Emailed', failed: 'Email failed', skipped: 'Not emailed (no email on file)' };

export const HelpConnectCertificatesButton = ({ participantId }) => {
  const [open, setOpen] = useState(false);
  const [certs, setCerts] = useState(null);
  const [busy, setBusy] = useState(null);
  const [defaultEmail, setDefaultEmail] = useState('');
  const [resendId, setResendId] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!open || !participantId) return;
    axios.get(`${API}/participants/${participantId}/helpconnect-certificates`)
      .then((r) => { setCerts(r.data.certificates); setDefaultEmail(r.data.default_email || ''); })
      .catch(() => { setCerts([]); toast.error('Could not load HELPConnect certificates'); });
  }, [open, participantId, reload]);

  const download = async (c) => {
    setBusy(c.id);
    try {
      const res = await axios.get(`${API}/participants/${participantId}/helpconnect-certificates/${c.id}/download`, { responseType: 'blob' });
      const match = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : `HELPConnect-Certificate-${c.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error('Could not download the certificate');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} disabled={!participantId} data-testid="helpconnect-certificates-btn">
        <Award className="h-4 w-4 mr-2" /> HELPConnect Certificates
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="helpconnect-certificates-dialog">
          <DialogHeader>
            <DialogTitle>HELPConnect Certificates</DialogTitle>
            <DialogDescription>Certificates this participant has earned in the HELPConnect learning portal.</DialogDescription>
          </DialogHeader>
          {!certs && <Loader2 className="h-5 w-5 animate-spin" />}
          {certs && certs.length === 0 && <p className="text-sm text-muted-foreground" data-testid="helpconnect-certificates-empty">No certificates earned yet.</p>}
          {certs && certs.length > 0 && (
            <div className="divide-y rounded-lg border" data-testid="helpconnect-certificates-list">
              {certs.map((c) => (
                <div key={c.id} data-testid={`helpconnect-certificate-row-${c.id}`}>
                <div className="flex items-center gap-3 p-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{c.course_title}</div>
                    <div className="text-xs text-muted-foreground">
                      No. {c.number} · Completed {c.completion_date}{c.email_status ? ` · ${EMAIL_LABEL[c.email_status] || c.email_status}` : ''}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => download(c)} disabled={busy === c.id} aria-label={`Download ${c.course_title} certificate`} title="Download PDF" data-testid={`helpconnect-certificate-download-${c.id}`}>
                    {busy === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setResendId(resendId === c.id ? null : c.id)} aria-label={`Resend ${c.course_title} certificate`} title="Resend by email" data-testid={`helpconnect-certificate-resend-${c.id}`}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                {resendId === c.id && (
                  <HelpConnectCertificateResend participantId={participantId} cert={c} defaultEmail={defaultEmail}
                    onCancel={() => setResendId(null)} onSent={() => { setResendId(null); setReload((n) => n + 1); }} />
                )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
